import type { ApiPaginatedResponse, ApiResponse } from '@bcube/shared';
import type { ProposalDeliveryFields, ProposalDeliveryMode } from '@bcube/shared/constants';
import { api } from '@/lib/api';
import type { AccountContact } from './accounts';
import type { CreditAnalysis } from './credit-analysis';

export enum ProposalStatus {
  OPEN = 'OPEN',
  FINISHED = 'FINISHED',
  CANCELED = 'CANCELED',
  PENDING_CREDIT = 'PENDING_CREDIT',
  PENDING_ACCEPT = 'PENDING_ACCEPT',
  REFUSED = 'REFUSED',
  EXPIRED = 'EXPIRED',
  ACCEPTED_PENDING_PAYMENT = 'ACCEPTED_PENDING_PAYMENT',
}

export interface Proposal extends ProposalDeliveryFields {
  id: string;
  seq: number;
  clientId: string;
  clientName: string;
  clientCpfCnpj: string;
  userId: string;
  userName: string;
  status: ProposalStatus;
  creditAnalysisId?: string | null;
  deliveryAddressId?: string | null;
  contact?: AccountContact | null;
  payment?: ProposalPayment | null;
  sentAt?: string | null;
  created_at: string;
}

export interface ProposalQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: ProposalStatus;
  clientId?: string;
  userId?: string;
  code?: number;
  seller?: string;
  start_date?: string;
  end_date?: string;
}

export interface PaginatedResponse<T> {
  docs: T[];
  total: number;
  pages: number;
  limit: number;
}

export interface CreateProposalPayload {
  clientId: string;
  clientName?: string;
  clientCpfCnpj: string;
  status: ProposalStatus;
  contact?: AccountContact | null;
}

export interface ProposalPayment {
  paymentType: 'CASH' | 'INSTALLMENTS';
  dueDay: number;
  installments: number;
  entradaInCents: number;
  freteInCents: number;
  shippingIncludedInDownPayment: boolean;
  hasDownPayment?: boolean;
  downPaymentPercentage?: number;
  downPaymentByBankSlip?: boolean;
  prorrogarEntrada?: boolean;
  entradaProrrogadaEm?: string | null;
  boletoAvista?: boolean;
}

export interface UpdateProposalPayload {
  deliveryMode?: ProposalDeliveryMode;
  pickupLocationId?: string | null;
  fiscalAddressId?: string | null;
  clientId?: string;
  clientName?: string;
  clientCpfCnpj?: string;
  status?: ProposalStatus;
  creditAnalysisId?: string | null;
  deliveryAddressId?: string | null;
  contact?: AccountContact | null;
}

export interface ProposalPaymentPayload {
  paymentType: 'CASH' | 'INSTALLMENTS';
  dueDay: number;
  installments: number;
  freteInCents: number;
  shippingIncludedInDownPayment: boolean;
  hasDownPayment?: boolean;
  downPaymentPercentage?: number;
  downPaymentByBankSlip?: boolean;
  prorrogarEntrada?: boolean;
  entradaProrrogadaEm?: string | null;
  boletoAvista?: boolean;
}

export interface ProposalSendEmailResult {
  sentTo: string;
  proposalNumber: number;
  sentDate: string;
  validityDate: string;
}

export type ProposalCreditAnalysisResolutionStatus =
  | 'VALID'
  | 'PENDING'
  | 'EXPIRED'
  | 'EXCLUSIVE_OTHER_PROPOSAL'
  | 'NONE';

export interface ProposalCreditAnalysisResolution {
  status: ProposalCreditAnalysisResolutionStatus;
  isValid: boolean;
  analysis: CreditAnalysis | null;
  message: string;
  source: 'EXPLICIT' | 'ACCOUNT' | 'NONE';
}

export type ProposalAcceptanceAction =
  | 'LINK_SENT'
  | 'LINK_VIEWED'
  | 'OTP_SENT'
  | 'OTP_RESENT'
  | 'OTP_VERIFIED'
  | 'DETAILS_VIEWED'
  | 'REFUSED'
  | 'ACCEPTED'
  | 'EXPIRED';

export interface ProposalAcceptanceHistoryItem {
  occurredAt: string;
  actorEmail: string | null;
  actorIp: string | null;
  action: ProposalAcceptanceAction;
  detail: string | null;
}

export const proposalApi = {
  findMany: async (params?: ProposalQueryParams): Promise<PaginatedResponse<Proposal>> => {
    const { data } = await api.get<ApiPaginatedResponse<Proposal>>('/commercial/proposals', {
      params,
    });
    return {
      docs: data.data,
      total: data.meta.total,
      pages: data.meta.pages,
      limit: data.meta.limit,
    };
  },

  findOne: async (id: string): Promise<Proposal> => {
    const { data } = await api.get<ApiResponse<Proposal>>(`/commercial/proposals/${id}`);
    return data.data;
  },

  resolveCreditAnalysis: async (id: string): Promise<ProposalCreditAnalysisResolution> => {
    const { data } = await api.get<ApiResponse<ProposalCreditAnalysisResolution>>(
      `/commercial/proposals/${id}/credit-analysis-resolution`,
    );
    return data.data;
  },

  create: async (payload: CreateProposalPayload): Promise<{ success: boolean; id?: string }> => {
    const { data } = await api.post<ApiResponse<{ id?: string }>>('/commercial/proposals', payload);
    return { success: true, id: data.data.id };
  },

  update: async (id: string, payload: UpdateProposalPayload): Promise<Proposal> => {
    const { data } = await api.patch<ApiResponse<Proposal>>(`/commercial/proposals/${id}`, payload);
    return data.data;
  },

  setDelivery: async (id: string, payload: UpdateProposalPayload): Promise<Proposal> => {
    const { data } = await api.patch<ApiResponse<Proposal>>(
      `/commercial/proposals/${id}/delivery`,
      payload,
    );
    return data.data;
  },

  setPayment: async (id: string, payment: ProposalPaymentPayload): Promise<Proposal> => {
    const { data } = await api.patch<ApiResponse<Proposal>>(
      `/commercial/proposals/${id}/payment`,
      payment,
    );
    return data.data;
  },

  submit: async (id: string): Promise<{ status: string }> => {
    const { data } = await api.post<ApiResponse<{ status: string }>>(
      `/commercial/proposals/${id}/submit`,
    );
    return data.data;
  },

  resend: async (id: string): Promise<ProposalSendEmailResult> => {
    const { data } = await api.post<ApiResponse<ProposalSendEmailResult>>(
      `/commercial/proposals/${id}/send-email`,
    );
    return data.data;
  },

  cancel: async (id: string): Promise<void> => {
    await api.post(`/commercial/proposals/${id}/cancel`);
  },

  reopen: async (id: string): Promise<Proposal> => {
    const { data } = await api.post<ApiResponse<Proposal>>(`/commercial/proposals/${id}/reopen`);
    return data.data;
  },

  getAcceptanceHistory: async (id: string): Promise<ProposalAcceptanceHistoryItem[]> => {
    const { data } = await api.get<ApiResponse<{ docs: ProposalAcceptanceHistoryItem[] }>>(
      `/commercial/proposals/${id}/acceptance-history`,
    );
    return data.data.docs;
  },
};
